import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useReduxState } from "hooks/useReduxState";
import {
  setPersonalLimit,
  setPersonalListLimit,
  setPersonalOffset,
  setPersonalSortValue,
  setPersonalKeyword,
  setIsSearch
} from "modules/binders/list/actions";
import { setPersonalCardViewMode } from "modules/binders/saved/actions";
import { useToast } from "hooks/useToast";
import { actionCreators as headerActionCreators } from "modules/header";
import { request } from "utils/apiClient";
import { useLocation } from "react-router";
import { LAZY_LOAD_LIMIT } from "features/BinderPage/List";
import { trackAction } from "utils/helper/analytics";
import { useMergeCollection } from "../../useMergeCollection";
import { useRequestMutation } from "hooks/useRequest";
import { BinderItem } from "models/BinderItem";

type ErrorType = {
  message: string;
  body: {
    code: number;
    details: {
      apiErrorCode: string;
      message: string;
      stackTrace?: string;
      errorType?: string;
    }[];
  };
};

export const usePersonalBinder = ({
  isHome = false
}: { isHome?: boolean } = {}) => {
  const dispatch = useDispatch();
  const offset = useReduxState(s => s.binders.list.personalOffset);
  const limit = useReduxState(s => s.binders.list.personalLimit);
  const listLimit = useReduxState(s => s.binders.list.personalListLimit);
  const sortValue = useReduxState(s => s.binders.list.personalSortValue);
  const personalKeyword = useReduxState(s => s.binders.list.personalKeyword);
  const isSearch = useReduxState(s => s.binders.list.isSearch);
  const cardViewMode = useReduxState(s => s.binders.saved.personalCardViewMode);
  const [isError, setIsError] = useState(false);
  const toast = useToast();
  const location = useLocation();
  const { trigger: fetchNikkeiCollectionTrigger } = useRequestMutation<{
    binderList: BinderItem[];
    totalCount: number;
  }>(`${isHome ? "/home" : ""}/binder/personal`, "GET");
  const {
    collections,
    isLoading,
    isAdding,
    totalCount,
    fetchCollections,
    addCollection
  } = useMergeCollection(fetchNikkeiCollectionTrigger, "personal");

  const fetchErrorHandle = useCallback(
    (error: ErrorType) => {
      const errors = [];
      if (error && error.body && error.body.details) {
        for (const detail of error.body.details) {
          errors.push(detail.message);
        }
      }
      toast.setErrors(
        error.message === "GATEWAY_TIMEOUT"
          ? "コレクションの読み込みがタイムアウトしました"
          : errors
      );
      setIsError(error.message === "GATEWAY_TIMEOUT");
    },
    [toast]
  );

  const fetchPersonalBinders = useCallback(
    async (searchLimit: number) => {
      const setLimit = cardViewMode
        ? LAZY_LOAD_LIMIT.card
        : searchLimit
        ? searchLimit
        : limit;
      try {
        dispatch(setPersonalOffset(0));
        dispatch(setPersonalLimit(setLimit));
        dispatch(setIsSearch(!!personalKeyword));
        dispatch(
          setPersonalSortValue(
            !personalKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: location.pathname === "/" || cardViewMode ? 1 : 0,
          offset: 0,
          limit: setLimit,
          sortKbn: !personalKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      cardViewMode,
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      limit,
      location.pathname,
      personalKeyword,
      sortValue
    ]
  );

  const addPersonalBinders = useCallback(async () => {
    if (offset + limit < totalCount) {
      dispatch(setPersonalOffset(offset + limit));
      addCollection({
        needFeedArticles: cardViewMode ? 1 : 0,
        limit,
        offset: offset + limit,
        sortKbn: sortValue,
        keyword: personalKeyword
      });
    }
  }, [
    cardViewMode,
    dispatch,
    addCollection,
    limit,
    offset,
    personalKeyword,
    sortValue,
    totalCount
  ]);

  const sortPersonalBinder = async (sortKbn: string) => {
    try {
      dispatch(setPersonalOffset(0));
      dispatch(setIsSearch(!!personalKeyword));
      dispatch(
        setPersonalSortValue(
          !personalKeyword && sortKbn === "3" ? "1" : sortKbn
        )
      );
      fetchCollections({
        needFeedArticles: cardViewMode ? 1 : 0,
        sortKbn,
        offset: 0,
        limit,
        keyword: personalKeyword
      });
    } catch (error) {
      fetchErrorHandle(error as ErrorType);
    }
  };

  const deletePersonalBinder = async (binderId: string) => {
    try {
      const response: { binderId: string } = await request(
        "/binder/delete",
        { binderId },
        "POST"
      );
      dispatch(
        headerActionCreators.requestUpdate({ shouldLoadCollection: true })
      );
      return { success: true, binderId: response ? response.binderId : "" };
    } catch (error) {
      return {
        success: false,
        messages:
          (error as ErrorType).body?.details?.map(
            (item: any) => item.message
          ) ?? ""
      };
    }
  };

  const setCardViewMode = (isCardMode: boolean) => {
    dispatch(setPersonalCardViewMode(isCardMode));
  };

  const switchCardViewMode = useCallback(
    async (cardMode: boolean) => {
      try {
        const changedLimit = cardMode ? LAZY_LOAD_LIMIT.card : listLimit;
        if (!cardMode) {
          dispatch(setPersonalListLimit(changedLimit));
        }
        dispatch(setPersonalLimit(changedLimit));
        dispatch(setPersonalCardViewMode(cardMode));
        dispatch(setPersonalOffset(0));
        dispatch(setIsSearch(!!personalKeyword));
        dispatch(
          setPersonalSortValue(
            !personalKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardMode ? 1 : 0,
          sortKbn: !personalKeyword && sortValue === "3" ? "1" : sortValue,
          limit: changedLimit,
          keyword: personalKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      listLimit,
      personalKeyword,
      sortValue
    ]
  );

  const changePage = useCallback(
    async (offset: number, limit: number) => {
      try {
        dispatch(setPersonalOffset(offset));
        dispatch(setPersonalLimit(limit));
        dispatch(setIsSearch(!!personalKeyword));
        dispatch(
          setPersonalSortValue(
            !personalKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset,
          limit,
          sortKbn: !personalKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      personalKeyword,
      sortValue,
      fetchCollections,
      cardViewMode,
      fetchErrorHandle
    ]
  );

  const changeLimit = useCallback(
    async (limit: number) => {
      try {
        dispatch(setPersonalLimit(limit));
        dispatch(setPersonalListLimit(limit));
        dispatch(setPersonalOffset(0));
        dispatch(setIsSearch(!!personalKeyword));
        dispatch(
          setPersonalSortValue(
            !personalKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          sortKbn: !personalKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: personalKeyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      cardViewMode,
      dispatch,
      fetchCollections,
      fetchErrorHandle,
      personalKeyword,
      sortValue
    ]
  );

  const onKeywordSearch = useCallback(
    async (keyword: string) => {
      try {
        trackAction("myCollectionSearchKeyword", keyword);
        dispatch(setPersonalKeyword(keyword));
        dispatch(setIsSearch(!!keyword));
        dispatch(setPersonalOffset(0));
        dispatch(setPersonalSortValue(keyword ? "3" : "1"));
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          sortKbn: keyword ? "3" : "1",
          keyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [dispatch, fetchCollections, cardViewMode, limit, fetchErrorHandle]
  );

  return {
    isLoading,
    sortValue,
    isError,
    binderItems: collections,
    totalCount,
    fetchPersonalBinders,
    sortPersonalBinder,
    deletePersonalBinder,
    setCardViewMode,
    cardViewMode,
    addPersonalBinders,
    isAdding,
    limit,
    offset,
    switchCardViewMode,
    changePage,
    changeLimit,
    onKeywordSearch,
    personalKeyword,
    setPersonalKeyword,
    isSearch
  };
};
